/**
 * AASB 107 — Statement of Cash Flows. Rulepack v1.0.0.
 * Validates the integrity of a cash-flow statement: opening + Operating + Investing
 * + Financing = Closing. Method-recognition. Direct-method components reconcile.
 */
import { Finding, ValidationResult, DISCLAIMER } from './types';
import {
  CellValue, parseNumber, makeHeaderMap, normaliseHeader,
  acceptedHeadersFor, findingBuilder, severityCounts, compliantCount, fmt,
} from './_shared';

const VERSION = 'aasb107-1.0.0';
const fb = findingBuilder('AASB_107' as any, VERSION);

const HMAP = makeHeaderMap([
  ['period', 'period', 'fy', 'reporting period'],
  ['opening_cash', 'opening cash', 'cash at beginning'],
  ['operating_cf', 'operating cf', 'cash flows from operating', 'operating cash flow'],
  ['investing_cf', 'investing cf', 'cash flows from investing', 'investing cash flow'],
  ['financing_cf', 'financing cf', 'cash flows from financing', 'financing cash flow'],
  ['fx_effect', 'fx effect', 'effect of exchange rates', 'fx movement'],
  ['closing_cash', 'closing cash', 'cash at end'],
  ['method', 'method', 'cf method', 'direct or indirect'],
  // Direct-method components:
  ['receipts_customers', 'receipts from customers', 'cash receipts customers'],
  ['payments_suppliers', 'payments to suppliers', 'cash payments suppliers'],
  ['payments_employees', 'payments to employees', 'cash payments employees'],
  ['interest_paid', 'interest paid'],
  ['tax_paid', 'tax paid', 'income tax paid'],
]);

const REQUIRED = ['opening_cash', 'operating_cf', 'investing_cf', 'financing_cf', 'closing_cash'];

export function detectAASB107(headers: string[]) {
  const hits = new Set<string>();
  for (const r of headers) { const c = normaliseHeader(HMAP, String(r ?? '')); if (c) hits.add(c); }
  const matched = REQUIRED.filter(r => hits.has(r));
  const boost = ['operating cf', 'investing cf', 'financing cf', 'cash flows from'].some(k => headers.some(h => String(h ?? '').toLowerCase().includes(k)));
  return { confidence: Math.min(1, (matched.length / REQUIRED.length) * (boost ? 1.5 : 1)), matched, missing: REQUIRED.filter(r => !hits.has(r)) };
}

export function validateAASB107(headers: string[], data: CellValue[][], _asAt: Date = new Date()): ValidationResult {
  const findings: Finding[] = [];
  const schemaErrors: string[] = [];
  const idx: Record<string, number> = {};
  headers.forEach((h, i) => { const c = normaliseHeader(HMAP, String(h ?? '')); if (c) idx[c] = i; });
  for (const f of REQUIRED) if (idx[f] === undefined) schemaErrors.push(`Missing column "${f}". Accepted: ${acceptedHeadersFor(HMAP, f).join(', ')}.`);
  if (schemaErrors.length) {
    return { summary: { standard: 'AASB_107' as any, rulepackVersion: VERSION, rowsTested: 0, compliant: 0, byseverity: { info: 0, warning: 0, error: 0 }, runAt: new Date().toISOString() }, findings, schemaErrors, disclaimer: DISCLAIMER };
  }

  const rowIds: string[] = [];
  data.forEach((raw, i) => {
    const rowNum = i + 2;
    const get = (k: string) => raw[idx[k]];
    const id = String(idx.period !== undefined ? (get('period') ?? `row-${rowNum}`) : `row-${rowNum}`);
    rowIds.push(id);

    const missing = REQUIRED.filter(f => { const v = get(f); return v == null || v === ''; });
    if (missing.length) { findings.push(fb.build('AASB107.R01', 'error', 'Required field missing', `Row ${rowNum} (${id}): missing ${missing.join(', ')}.`, { rowId: id })); return; }

    const open = parseNumber(get('opening_cash'))!;
    const op = parseNumber(get('operating_cf'))!;
    const inv = parseNumber(get('investing_cf'))!;
    const fin = parseNumber(get('financing_cf'))!;
    const fxEff = idx.fx_effect !== undefined ? (parseNumber(get('fx_effect')) ?? 0) : 0;
    const close = parseNumber(get('closing_cash'))!;
    const method = String(get('method') ?? '').toLowerCase().trim();

    // R02 integrity
    const expectedClose = open + op + inv + fin + fxEff;
    const tol = Math.max(50, 0.005 * Math.abs(open || 1000));
    if (Math.abs(close - expectedClose) > tol) {
      findings.push(fb.build('AASB107.R02', 'error', 'Cash flow does not reconcile',
        `Period ${id}: opening $${fmt(open)} + operating $${fmt(op)} + investing $${fmt(inv)} + financing $${fmt(fin)} + fx $${fmt(fxEff)} = $${fmt(expectedClose)}, but closing reported $${fmt(close)} (delta ${close >= expectedClose ? '+' : '−'}$${fmt(Math.abs(close - expectedClose))}).`,
        {
          rowId: id,
          evidence: { open, op, inv, fin, fxEff, expectedClose, reportedClose: close },
          suggestedAdjustment: { field: 'closing_cash', from: close, to: +expectedClose.toFixed(2), rationale: 'Sum of opening + operating + investing + financing + FX.' },
        }));
    }

    // R03 method
    if (method && !/direct|indirect/.test(method)) {
      findings.push(fb.build('AASB107.R03', 'info', 'Method label not standard',
        `Period ${id}: method "${method}". AASB 107 supports direct method (encouraged for entity-level disclosure) or indirect method.`,
        { rowId: id }));
    }

    // R04 direct method components reconciliation
    if (/direct/.test(method)) {
      const components = ['receipts_customers', 'payments_suppliers', 'payments_employees', 'interest_paid', 'tax_paid'];
      const presentComponents = components.filter(c => idx[c] !== undefined);
      if (presentComponents.length >= 3) {
        let netOp = 0;
        for (const c of components) {
          if (idx[c] !== undefined) netOp += parseNumber(get(c)) ?? 0;
        }
        const compTol = Math.max(100, 0.02 * Math.abs(op || 1));
        if (Math.abs(netOp - op) > compTol) {
          findings.push(fb.build('AASB107.R04', 'warning', 'Direct-method components do not aggregate to operating CF',
            `Period ${id}: sum of receipts + payments + interest + tax = $${fmt(netOp)}, reported operating CF $${fmt(op)}. Direct method requires major classes to be presented separately and reconcile.`,
            { rowId: id, evidence: { sum: netOp, operating: op } }));
        }
      }
    }
  });

  return {
    summary: { standard: 'AASB_107' as any, rulepackVersion: VERSION, rowsTested: rowIds.length, compliant: compliantCount(rowIds, findings), byseverity: severityCounts(findings), runAt: new Date().toISOString() },
    findings, disclaimer: DISCLAIMER,
  };
}
